import { useEffect, useState, useCallback } from "react";
import { api, errText } from "../lib/api";
import { Button } from "./ui/button";
import { ShieldCheck, Loader2, Check, X, Copy, Gamepad2, Link2, Unlink, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

// VirtuCity Citizen verification controls — Discord OAuth2 link + FiveM one-time code pairing.
export function VirtuCityCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => api.get("/virtucity/status").then(({ data }) => setSt(data)).catch(() => setSt({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);

  const connectDiscord = async () => {
    setBusy("discord");
    try { const { data } = await api.get("/virtucity/discord/start"); window.location.href = data.url; }
    catch (e) { toast.error(errText(e)); setBusy(""); }
  };
  const disconnectDiscord = async () => {
    setBusy("discord");
    try { await api.post("/virtucity/discord/disconnect"); toast.success("Discord disconnected"); await load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const genCode = async () => {
    setBusy("fivem");
    try { await api.post("/virtucity/fivem/start"); await load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const cancelCode = async () => {
    setBusy("fivem");
    try { await api.post("/virtucity/fivem/cancel"); await load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const unlinkFivem = async () => {
    setBusy("fivem");
    try { await api.post("/virtucity/fivem/unlink"); toast.success("FiveM license unlinked"); await load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  if (!st) return (
    <div className="surface rounded-2xl p-5 mt-5" data-testid="virtucity-settings">
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-3"><Loader2 size={15} className="animate-spin" /> Loading VirtuCity…</div>
    </div>
  );

  const d = st.discord || {}, fm = st.fivem || {};
  const code = st.pending_code;
  const secsLeft = code ? Math.max(0, Math.round((new Date(code.expires_at).getTime() - now) / 1000)) : 0;
  const mmss = `${Math.floor(secsLeft / 60)}:${String(secsLeft % 60).padStart(2, "0")}`;

  return (
    <div className="surface rounded-2xl p-5 mt-5" data-testid="virtucity-settings">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className="text-gold" />
        <h2 className="font-display font-bold text-lg">VirtuCity Citizen</h2>
        {st.is_citizen && <span className="ml-1 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1" data-testid="vc-verified-pill"><Check size={11} />Verified</span>}
      </div>
      <p className="text-xs text-muted-foreground mb-4">Link your Discord and FiveM identity to enter the VirtuCity FiveM server. Both must belong to this VirtuStars account.</p>

      {st.banned && <div className="flex items-center gap-2 text-sm rounded-xl p-3 mb-4 bg-rose-500/10 text-rose-400 border border-rose-500/30" data-testid="vc-banned"><AlertTriangle size={16} />Your VirtuCity access has been banned. Contact an admin.</div>}
      {st.revoked && !st.banned && <div className="flex items-center gap-2 text-sm rounded-xl p-3 mb-4 bg-amber-500/10 text-amber-400 border border-amber-500/30" data-testid="vc-revoked"><AlertTriangle size={16} />Your verification was revoked. Re-link below or contact an admin.</div>}

      {/* Discord */}
      <div className="surface-2 rounded-xl p-4 mb-3" data-testid="vc-discord-block">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#5865F2]/20 flex items-center justify-center shrink-0"><Link2 size={18} className="text-[#8b93ff]" /></div>
            <div className="min-w-0">
              <div className="text-sm font-semibold">Discord</div>
              {d.linked ? <div className="text-[11px] text-emerald-400 flex items-center gap-1 truncate"><Check size={11} />Connected{d.username ? ` · @${d.username}` : ""}</div>
                : <div className="text-[11px] text-muted-foreground">Not connected</div>}
            </div>
          </div>
          {d.linked
            ? <Button onClick={disconnectDiscord} disabled={busy === "discord"} variant="outline" size="sm" className="rounded-lg border-white/15 bg-white/5 text-rose-400 shrink-0" data-testid="vc-discord-disconnect">{busy === "discord" ? <Loader2 size={14} className="animate-spin" /> : <><Unlink size={14} className="mr-1" />Disconnect</>}</Button>
            : <Button onClick={connectDiscord} disabled={busy === "discord" || !st.configured} size="sm" className="rounded-lg shrink-0 text-white" style={{ background: "#5865F2" }} data-testid="vc-discord-connect">{busy === "discord" ? <Loader2 size={14} className="animate-spin" /> : "Connect Discord"}</Button>}
        </div>
        {!st.configured && <div className="text-[11px] text-amber-400 mt-2">Discord linking is not configured yet.</div>}
      </div>

      {/* FiveM */}
      <div className="surface-2 rounded-xl p-4" data-testid="vc-fivem-block">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-orange-500/15 flex items-center justify-center shrink-0"><Gamepad2 size={18} className="text-orange-400" /></div>
            <div className="min-w-0">
              <div className="text-sm font-semibold">FiveM license</div>
              {fm.linked ? <div className="text-[11px] text-emerald-400 flex items-center gap-1 truncate"><Check size={11} />Verified · <span className="font-mono">{fm.license_masked}</span></div>
                : <div className="text-[11px] text-muted-foreground">Not verified</div>}
            </div>
          </div>
          {fm.linked
            ? <Button onClick={unlinkFivem} disabled={busy === "fivem"} variant="outline" size="sm" className="rounded-lg border-white/15 bg-white/5 text-rose-400 shrink-0" data-testid="vc-fivem-unlink">{busy === "fivem" ? <Loader2 size={14} className="animate-spin" /> : <><Unlink size={14} className="mr-1" />Unlink</>}</Button>
            : !code
              ? <Button onClick={genCode} disabled={busy === "fivem"} size="sm" className="btn-gold rounded-lg shrink-0" data-testid="vc-fivem-generate">{busy === "fivem" ? <Loader2 size={14} className="animate-spin" /> : "Link FiveM"}</Button>
              : null}
        </div>

        {!fm.linked && code && (
          <div className="mt-3 rounded-xl border border-gold/30 bg-gold/5 p-4" data-testid="vc-fivem-code">
            <div className="text-xs text-muted-foreground mb-2">Join the VirtuCity FiveM server and run this in chat:</div>
            <div className="flex items-center gap-2 mb-2">
              <code className="font-mono text-sm surface rounded-lg px-3 py-2">/virtulink {code.code}</code>
              <button onClick={() => { navigator.clipboard?.writeText(`/virtulink ${code.code}`); toast.success("Copied"); }} className="text-eblue" data-testid="vc-code-copy"><Copy size={15} /></button>
            </div>
            <div className="flex items-center justify-between">
              <div className={`text-[11px] flex items-center gap-1 ${secsLeft <= 30 ? "text-rose-400" : "text-muted-foreground"}`}><Clock size={11} />Expires in {mmss}</div>
              <button onClick={cancelCode} className="text-[11px] text-muted-foreground hover:text-rose-400" data-testid="vc-code-cancel">Cancel</button>
            </div>
            {secsLeft <= 0 && <button onClick={genCode} className="text-[11px] text-eblue mt-2" data-testid="vc-code-regen">Generate a new code</button>}
          </div>
        )}
      </div>

      {st.is_citizen && <div className="mt-4 flex items-center gap-2 text-sm text-emerald-400" data-testid="vc-citizen-confirm"><ShieldCheck size={16} />You're a verified VirtuCity Citizen. Entry to the FiveM server is enabled.</div>}
    </div>
  );
}
